import { useCallback, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import type { BlobRow } from '../sync/db.ts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { PhotoUnreadableError, prepareImage, type PreparedPhoto } from './capture.ts'
import { addPhoto, PhotoQuotaError, type PhotoLink } from './store.ts'

export interface LinkRow { id: string; site_id: string; file_id: string; sha256: string; bytes: number; mime: string; entity_type: string; entity_id: string; purpose: string; caption: string | null; captured_at: string | null; hidden_at: string | null }

/** The photos that are evidence for one record: the server's links with this device's pending ones laid over them, oldest first. */
export function useEntityPhotos(entityId: string): Array<Pending<LinkRow>> {
  const links = useMerged<LinkRow>('attachment_link', 'core.attachment_link')
  return useMemo(() => (links ?? []).filter((l) => l.entity_id === entityId && !l.hidden_at).sort((a, b) => (a.captured_at ?? '').localeCompare(b.captured_at ?? '') || a.id.localeCompare(b.id)), [links, entityId])
}

/** The pictures this device still holds for the server (by hash), live. */
export function useBlobs(): Map<string, BlobRow> {
  const { db } = useClub()
  const rows = useLiveQuery(() => db.blobs.toArray(), [db], [] as BlobRow[])
  return useMemo(() => new Map(rows.map((r) => [r.sha256, r])), [rows])
}
export function usePhotosWaiting(): { waiting: number; failed: number; rows: BlobRow[] } {
  const blobs = useBlobs()
  return useMemo(() => {
    const rows = [...blobs.values()]
    return { waiting: rows.filter((r) => r.state !== 'failed').length, failed: rows.filter((r) => r.state === 'failed').length, rows }
  }, [blobs])
}

/** Taking pictures on a page. With `entity` each picture is registered at once (the record already exists on this device); without it the pictures are held until `commit(...)`
 *  (a form that creates its record on submit: the check, the defect, the finish). */
export function usePhotoDraft(entity?: PhotoLink & { dependsOn?: string[] }) {
  const { t } = useTranslation()
  const { db, org, site, send } = useClub()
  const [photos, setPhotos] = useState<PreparedPhoto[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fail = useCallback((e: unknown) => setError(e instanceof PhotoQuotaError ? t('photos.quota') : e instanceof PhotoUnreadableError ? t('photos.unreadable') : e instanceof Error && e.message ? e.message : t('errors.unknown')), [t])
  const addFile = useCallback(async (file: Blob) => {
    setBusy(true); setError(null)
    try {
      const p = await prepareImage(file)
      if (entity) await addPhoto(db, send, org.org_id, site.site_id, p, entity, entity.dependsOn?.length ? { dependsOn: entity.dependsOn } : {})
      else setPhotos((all) => (all.some((x) => x.sha256 === p.sha256) ? all : [...all, p]))
    } catch (e) { fail(e) } finally { setBusy(false) }
  }, [db, send, org.org_id, site.site_id, entity, fail])
  const remove = useCallback((sha: string) => setPhotos((all) => all.filter((p) => p.sha256 !== sha)), [])
  const commit = useCallback(async (link: PhotoLink, dependsOn?: string[]) => {
    for (const p of photos) await addPhoto(db, send, org.org_id, site.site_id, p, link, dependsOn?.length ? { dependsOn } : {})
    setPhotos([])
  }, [db, send, org.org_id, site.site_id, photos])
  return { photos, busy, error, addFile, remove, commit }
}
export type PhotoDraft = ReturnType<typeof usePhotoDraft>
